import { useCallback, useEffect, useState } from "react";
import { Bluetooth, BluetoothSearching, Check, Loader2, Printer, RefreshCw, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cardCls, blueBtn } from "@/components/mobile/parts";
import { allowedPrinters, btAvailable, btError, btPermission, btTest, choosePrinter, pairPrinter } from "@/lib/btprint";
import { cn } from "@/lib/utils";

const warn = "flex items-start gap-2 rounded-xl p-3 text-sm ring-1";

function Status({ st, onRetry }) {
  if (!st.avail) return <div className={cn(warn, "bg-amber-50 text-amber-900 ring-amber-200")} data-testid="m-bt-off">Bluetooth indisponível. Ligue o Bluetooth do celular e toque em Verificar de novo.</div>;
  if (st.perm === "denied") {
    return (
      <div className={cn(warn, "flex-col bg-red-50 text-red-900 ring-red-200")} data-testid="m-bt-denied">
        <span>O Bluetooth está bloqueado para este site. No Chrome, toque no cadeado ao lado do endereço, abra Permissões e libere Dispositivos próximos (Bluetooth).</span>
        <Button variant="outline" size="sm" onClick={onRetry} data-testid="m-bt-recheck-btn"><RefreshCw className="mr-1 h-4 w-4" />Verificar de novo</Button>
      </div>
    );
  }
  return null;
}

function DeviceList({ devices, current, busy, onSelect }) {
  if (!devices.length) return <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500" data-testid="m-bt-no-devices">Nenhuma impressora liberada neste celular ainda.</p>;
  return (
    <div className="space-y-2" data-testid="m-bt-device-list">
      {devices.map((d) => {
        const on = d.id === current;
        return (
          <button key={d.id} type="button" onClick={() => onSelect(d)} disabled={!!busy}
            className={cn("flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors", on ? "border-[#1565c0] bg-[#e3f2fd]" : "bg-white hover:bg-slate-50")} data-testid={`m-bt-device-${d.id}`}>
            <Printer className="h-5 w-5 shrink-0 text-[#1565c0]" />
            <span className="min-w-0 flex-1 truncate font-semibold">{d.name || "Impressora Bluetooth"}</span>
            {busy === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : on ? <span className="flex items-center gap-1 text-xs font-semibold text-[#1565c0]"><Check className="h-4 w-4" />Selecionada</span> : <span className="text-xs text-slate-500">Selecionar</span>}
          </button>
        );
      })}
    </div>
  );
}

export function BrowserPrinterCard({ p, update, store }) {
  const [st, setSt] = useState({ checking: true, perm: "unknown", avail: true, devices: [] });
  const [busy, setBusy] = useState("");
  const check = useCallback(async () => {
    setSt((x) => ({ ...x, checking: true }));
    const [perm, avail, devices] = await Promise.all([btPermission(), btAvailable(), allowedPrinters()]);
    setSt({ checking: false, perm, avail, devices });
  }, []);
  useEffect(() => { check(); }, [check]);
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(btError(e)); } setBusy(""); };
  const select = (d) => run(d.id, async () => { const x = await choosePrinter(d); update(x); toast.success(`Impressora ${x.device_name} selecionada`); });
  const allow = () => run("allow", async () => { const x = await pairPrinter(); update(x); toast.success(`Impressora ${x.device_name} selecionada`); await check(); });
  const test = () => run("test", async () => { await btTest(p, store); toast.success("Teste enviado para a impressora"); });
  const forget = () => run("forget", async () => { await st.devices.find((d) => d.id === p.device_id)?.forget?.(); update({ device_id: "", device_name: "" }); await check(); });
  const firstTime = st.perm !== "granted" && !st.devices.length;
  const icon = (k, Icon) => (busy === k ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icon className="mr-2 h-4 w-4" />);
  return (
    <div className={cn(cardCls, "space-y-3 p-4")} data-testid="m-bt-printer-card">
      {st.checking ? <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="m-bt-checking"><Loader2 className="h-4 w-4 animate-spin" />Verificando o Bluetooth…</p> : (
        <>
          <Status st={st} onRetry={check} />
          <p className="text-sm font-semibold">Impressoras liberadas neste celular</p>
          <DeviceList devices={st.devices} current={p.device_id} busy={busy} onSelect={select} />
          <Button className={cn("h-12 w-full", blueBtn)} onClick={allow} disabled={!!busy || !st.avail || st.perm === "denied"} data-testid="m-bt-pair-btn">
            {icon("allow", firstTime ? Bluetooth : BluetoothSearching)}{firstTime ? "Permitir Bluetooth e escolher impressora" : "Escolher outra impressora pareada"}
          </Button>
          <p className="text-xs text-slate-500">Abre a janela do Chrome com as impressoras por perto: toque na sua. Na primeira vez o celular pede a permissão de Bluetooth.</p>
        </>
      )}
      {p.device_id && (
        <div className="space-y-2 border-t pt-3">
          <p className="text-sm">Em uso: <b data-testid="m-bt-printer-name">{p.device_name}</b></p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-11" onClick={test} disabled={!!busy} data-testid="m-bt-test-btn">{icon("test", Printer)}Imprimir teste</Button>
            <Button variant="outline" className="h-11 text-red-600" onClick={forget} disabled={!!busy} data-testid="m-bt-forget-btn"><Unlink className="mr-2 h-4 w-4" />Esquecer</Button>
          </div>
        </div>
      )}
    </div>
  );
}
